import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "../hooks/useApi";
import FormEvento from "./FormEvento";
import { cuentaRegresiva, diasHasta, formatearFecha } from "../utils/fechasEvento";
import { hrefSeguro } from "../utils/hrefSeguro";

// Listado de eventos con su alta, edición y borrado.
//
// Sirve a las dos puertas de entrada: el menú "Eventos" de un perfil
// individual (sin `artista`) y la ficha de un artista del roster (con
// `artista`, que además fija la atribución). La única diferencia es de dónde
// sale el `managed_artist_id`.
export default function ListaEventos({ artista, showSnack, titulo }) {
  const { loadApi, loadingApi } = useApi();
  const [eventos, setEventos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [formAbierto, setFormAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState(null);
  const [aBorrar, setABorrar] = useState(null);

  const filtro = artista ? `?managed_artist_id=${artista.id}` : "";

  const cargar = useCallback(() => {
    setCargando(true);
    loadApi(`artista/eventos${filtro}`, true, "get")
      .then((res) => setEventos(res.data || []))
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => setCargando(false));
  }, [filtro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardar = (form, flyer) => {
    const body = { ...form };
    // Los campos vacíos no se mandan: en un multipart viajarían como la
    // cadena "" y pisarían lo guardado con un valor inútil.
    Object.keys(body).forEach((k) => {
      if (body[k] === "" || body[k] === null) delete body[k];
    });
    if (artista) body.managed_artist_id = artista.id;
    if (flyer) body.flyer = flyer;

    const esEdicion = !!enEdicion;
    loadApi(
      esEdicion ? `artista/eventos/${enEdicion.id}` : "artista/eventos",
      true,
      esEdicion ? "put" : "post",
      body,
      true
    )
      .then((res) => {
        showSnack(res.data.message);
        setFormAbierto(false);
        setEnEdicion(null);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  const borrar = () => {
    loadApi(`artista/eventos/${aBorrar.id}`, true, "delete")
      .then((res) => {
        showSnack(res.data.message);
        setABorrar(null);
        cargar();
      })
      .catch((e) => {
        setABorrar(null);
        showSnack(e.message, "error");
      });
  };

  const guardando =
    loadingApi.includes("post__artista/eventos") ||
    loadingApi.includes(`put__artista/eventos/${enEdicion?.id}`);

  const encabezado = titulo || "Mis eventos";

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h6" fontWeight={700}>
          {encabezado}
        </Typography>
        {/* Una ficha dada de baja no recibe material nuevo. */}
        {(!artista || artista.activo) && (
          <Button
            variant="outlined"
            onClick={() => {
              setEnEdicion(null);
              setFormAbierto(true);
            }}
          >
            + Nuevo evento
          </Button>
        )}
      </Stack>

      {!cargando && eventos.length === 0 && (
        <Typography variant="body2" color="text.secondary" mt={2}>
          Todavía no hay eventos cargados
          {artista ? ` para ${artista.nombre}` : ""}.
        </Typography>
      )}

      <Stack gap={2} mt={2}>
        {eventos.map((evento) => {
          const dias = diasHasta(evento.fecha_inicio);
          const paso = dias !== null && dias < 0;
          return (
            <Card key={evento.id} sx={{ opacity: paso ? 0.6 : 1 }}>
              <CardContent>
                <Stack direction="row" gap={2}>
                  <Box
                    component="img"
                    src={evento.flyer}
                    alt={evento.nombre}
                    sx={{
                      width: 90,
                      height: 90,
                      objectFit: "contain",
                      borderRadius: 1,
                      bgcolor: "#f0f1f7",
                      flexShrink: 0,
                    }}
                  />
                  <Stack flex={1} minWidth={0} gap={0.5}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography fontWeight={700}>{evento.nombre}</Typography>
                      <Chip label={evento.tipo} size="small" />
                      {paso && <Chip label="Ya pasó" size="small" color="default" />}
                    </Stack>

                    {/* La fecha va destacada: es el dato que define todo lo
                        demás, incluido el plazo que tendrá el difusor. */}
                    <Typography variant="body2">
                      <strong>{formatearFecha(evento.fecha_inicio)}</strong>
                      {evento.fecha_fin
                        ? ` — ${formatearFecha(evento.fecha_fin)}`
                        : ""}{" "}
                      <Typography component="span" variant="body2" color="text.secondary">
                        ({cuentaRegresiva(evento.fecha_inicio)})
                      </Typography>
                    </Typography>

                    {(evento.lugar || evento.ciudad) && (
                      <Typography variant="body2" color="text.secondary">
                        {[evento.lugar, evento.ciudad].filter(Boolean).join(" · ")}
                      </Typography>
                    )}

                    {evento.fecha_recomendada && (
                      <Typography variant="caption" color="text.secondary">
                        Publicar la nota antes del{" "}
                        {formatearFecha(evento.fecha_recomendada)}
                      </Typography>
                    )}

                    <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                      {/* Un evento que ya pasó no se puede enviar: la nota
                          saldría después del show. */}
                      {!paso && (
                        <Button
                          size="small"
                          variant="contained"
                          component={Link}
                          to={`/artista/enviar-evento/${evento.id}`}
                        >
                          Enviar a difusores
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setEnEdicion(evento);
                          setFormAbierto(true);
                        }}
                      >
                        Editar
                      </Button>
                      {evento.url_entradas && (
                        <Button
                          size="small"
                          href={hrefSeguro(evento.url_entradas)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Entradas
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setABorrar(evento)}
                      >
                        Eliminar
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <FormEvento
        abierto={formAbierto}
        evento={enEdicion}
        artista={artista}
        onCerrar={() => {
          setFormAbierto(false);
          setEnEdicion(null);
        }}
        onGuardar={guardar}
        guardando={guardando}
        showSnack={showSnack}
      />

      <Dialog open={!!aBorrar} onClose={() => setABorrar(null)}>
        <DialogTitle>Eliminar {aBorrar?.nombre}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se elimina para siempre. Si ya lo enviaste a algún difusor no se
            puede borrar; en ese caso, edítalo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setABorrar(null)}>Cancelar</Button>
          <Button color="error" onClick={borrar}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
